import React, { useState } from "react";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { supportCode } from "@/lib/supportCode";

/**
 * Badge cliccabile che mostra il codice supporto interno (`AF-XXXXXX`).
 * Al click copia il codice negli appunti + toast di conferma.
 *
 * Perché serve: quando l'utente contatta il supporto, questo codice è
 * l'identificativo più veloce per trovargli la fattura giusta dall'admin
 * panel (indice DB su `id` prefix). Va reso il più visibile e copiabile
 * possibile.
 *
 * Varianti:
 *  - `size="sm"`: compatto, per liste
 *  - `size="md"` (default): standard, per header pagine
 *
 * `invoiceId` è l'UUID interno della fattura — se non passato, si usa
 * `code` (se già pre-calcolato via supportCode()).
 */
export default function SupportCodeBadge({ invoiceId, code, size = "md", className = "" }) {
  const [copied, setCopied] = useState(false);
  const value = code || supportCode(invoiceId);
  if (!value) return null;

  const copy = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`Codice ${value} copiato`, { duration: 1500 });
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Impossibile copiare");
    }
  };

  const sizeClasses =
    size === "sm"
      ? "px-2 py-0.5 text-[10px]"
      : "px-2.5 py-1 text-xs";

  return (
    <button
      onClick={copy}
      title="Codice supporto interno · click per copiare"
      data-testid={`support-code-badge-${invoiceId || value}`}
      className={`inline-flex items-center gap-1 rounded-md border border-[#1D4ED8]/25 bg-[#1D4ED8]/8 text-[#1D4ED8] font-mono font-bold hover:bg-[#1D4ED8]/15 hover:border-[#1D4ED8]/40 transition-colors ${sizeClasses} ${className}`}
    >
      <span>{value}</span>
      {copied ? (
        <Check className="w-3 h-3 shrink-0" />
      ) : (
        <Copy className="w-3 h-3 shrink-0 opacity-60" />
      )}
    </button>
  );
}
